/**
 * Registration Service
 * API service for registration-related operations.
 */

import { api, apiClient } from '@services/api';
import type {
  RegistrationLink,
  RegistrationLinkStatus,
  RegistrationLinksFilter,
  PaginationState,
  RegistrationLinksSummary,
} from '../types/registrationLink.types';
import type { CreateLinkFormData } from '../components/CreateLinkModal/types';
import type { PersonalInfoFormData, ChangePasswordFormData } from '@utils/validation';

/**
 * API endpoints for registration module.
 */
const ENDPOINTS = {
  LINKS: '/registration/links',
  LINK_BY_ID: (id: number) => `/registration/links/${id}`,
  LINK_REGISTRATIONS: (id: number) => `/registration/links/${id}/registrations`,
  LINK_SUMMARY: '/registration/links/summary',
  MANAGERS: '/registration/managers',
  DAIES_BY_MANAGER: (managerId: number) => `/registration/managers/${managerId}/daies`,
  PROFILE: '/profile',
  CHANGE_PASSWORD: '/profile/change-password',
  ONBOARDING: '/onboarding',
  PUBLIC_REGISTER: '/register',
} as const;

/**
 * Paginated response type.
 */
interface PaginatedResponse<T> {
  data: T[];
  meta: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

/**
 * Manager type.
 */
export interface Manager {
  id: number;
  userId: number;
  name: string;
  email?: string;
  phone?: string;
  type: string;
  region?: string;
  position?: string;
  daieCount?: number;
  joinDate?: string;
  contractExpiryDate?: string;
  contractStatus?: string;
}

/**
 * Daie type for selection.
 */
export interface Daie {
  id: number;
  userId: number;
  name: string;
  email: string;
  daieCode: string | null;
  status: string;
}

/**
 * Link registration (user registered under a link).
 */
export interface LinkRegistration {
  id: number;
  userId: number;
  fullName: string;
  email: string;
  phone: string;
  registeredAt: string;
  paymentStatus: string;
  paymentAmount: string;
  accountStatus: string;
  daieCode: string | null;
}

/**
 * Link registrations response.
 */
export interface LinkRegistrationsResponse {
  data: LinkRegistration[];
  link: {
    id: number;
    linkName: string;
  };
  meta: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

/**
 * Registration Links API.
 */
export const registrationLinksApi = {
  /**
   * Fetches registration links with filters and pagination.
   */
  getLinks: async (
    filters: RegistrationLinksFilter,
    pagination: PaginationState
  ): Promise<PaginatedResponse<RegistrationLink>> => {
    const params = {
      search: filters.search || undefined,
      status: filters.status !== 'all' ? filters.status : undefined,
      type: filters.type !== 'all' ? filters.type : undefined,
      manager: filters.manager !== 'all' ? filters.manager : undefined,
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
    };

    return api.get<PaginatedResponse<RegistrationLink>>(ENDPOINTS.LINKS, { params });
  },

  /**
   * Fetches a single registration link by ID.
   */
  getLinkById: async (id: number): Promise<RegistrationLink> => {
    return api.get<RegistrationLink>(ENDPOINTS.LINK_BY_ID(id));
  },

  /**
   * Creates a new registration link.
   */
  createLink: async (data: CreateLinkFormData): Promise<RegistrationLink> => {
    const response = await api.post<{ success: boolean; data: RegistrationLink }>(ENDPOINTS.LINKS, data);
    return response.data;
  },

  /**
   * Updates an existing registration link.
   */
  updateLink: async (
    id: number,
    data: Partial<CreateLinkFormData>
  ): Promise<RegistrationLink> => {
    return api.put<RegistrationLink>(ENDPOINTS.LINK_BY_ID(id), data);
  },

  /**
   * Terminates (deactivates) a registration link.
   */
  terminateLink: async (id: number): Promise<void> => {
    return api.patch<void>(ENDPOINTS.LINK_BY_ID(id), { status: 'inactive' });
  },

  /**
   * Deletes a registration link.
   */
  deleteLink: async (id: number): Promise<void> => {
    return api.delete<void>(ENDPOINTS.LINK_BY_ID(id));
  },

  /**
   * Fetches registration links summary.
   */
  getSummary: async (): Promise<RegistrationLinksSummary> => {
    return api.get<RegistrationLinksSummary>(ENDPOINTS.LINK_SUMMARY);
  },

  /**
   * Exports registration links as CSV.
   */
  exportCSV: async (filters: RegistrationLinksFilter): Promise<Blob> => {
    const params = {
      status: filters.status !== 'all' ? filters.status : undefined,
      type: filters.type !== 'all' ? filters.type : undefined,
      manager: filters.manager !== 'all' ? filters.manager : undefined,
    };

    const response = await api.get<Blob>(`${ENDPOINTS.LINKS}/export`, {
      params,
      responseType: 'blob',
    });

    return response;
  },

  /**
   * Fetches list of managers for dropdown selection.
   */
  getManagers: async (): Promise<Manager[]> => {
    const response = await api.get<{ success: boolean; data: Manager[] }>(ENDPOINTS.MANAGERS);
    return response.data || [];
  },

  /**
   * Fetches daies under a specific manager.
   */
  getDaiesByManager: async (managerId: number): Promise<{ daies: Daie[]; manager: { id: number; name: string } }> => {
    const response = await api.get<{ success: boolean; data: Daie[]; manager: { id: number; name: string } }>(
      ENDPOINTS.DAIES_BY_MANAGER(managerId)
    );
    return {
      daies: response.data || [],
      manager: response.manager,
    };
  },

  /**
   * Bulk updates registration links status.
   */
  bulkUpdateLinks: async (
    linkIds: number[],
    status: RegistrationLinkStatus
  ): Promise<void> => {
    return api.post<void>(`${ENDPOINTS.LINKS}/bulk-update`, {
      linkIds,
      status,
    });
  },

  /**
   * Fetches registrations (users) under a specific registration link.
   */
  getLinkRegistrations: async (
    linkId: number,
    pagination: { currentPage: number; itemsPerPage: number }
  ): Promise<LinkRegistrationsResponse> => {
    const params = {
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
    };

    return api.get<LinkRegistrationsResponse>(ENDPOINTS.LINK_REGISTRATIONS(linkId), { params });
  },
};

/**
 * Profile response data structure.
 */
export interface ProfileResponse {
  success: boolean;
  data: {
    registrationType: 'individual' | 'company';
    personal: {
      fullName: string;
      icNumber?: string;
      ssmNumber?: string;
      email: string;
      phone: string;
      gender?: string;
      educationLevel?: string;
      workingExperienceHistory?: string;
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state?: string;
      postcode?: string;
    };
    bank: {
      bankName?: string;
      accountNumber?: string;
      accountHolderName?: string;
    };
    emergencyContact: {
      name?: string;
      phone?: string;
      relationship?: string;
    };
    contract: {
      daieCode: string;
      status: string;
      contractStart?: string;
      contractEnd?: string;
      signatureName?: string;
      contractSignedAt?: string;
    };
    user: {
      id: number;
      name: string;
      email: string;
      phone?: string;
      createdAt: string;
    };
  };
}

/**
 * Sponsored Daie data structure.
 */
export interface SponsoredDaie {
  id: number;
  userId: number;
  daieCode: string | null;
  fullName: string;
  email: string;
  phone: string | null;
  status: string;
  registeredAt: string | null;
}

/**
 * Sponsored Daies response.
 */
export interface SponsoredDaiesResponse {
  success: boolean;
  data?: SponsoredDaie[];
  message?: string;
}

/**
 * Profile update data structure.
 */
export interface ProfileUpdateData {
  personal?: {
    fullName?: string;
    email?: string;
    phone?: string;
    gender?: string;
    educationLevel?: string;
    workingExperienceHistory?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postcode?: string;
  };
  bank?: {
    bankName?: string;
    accountNumber?: string;
    accountHolderName?: string;
  };
  emergencyContact?: {
    name?: string;
    phone?: string;
    relationship?: string;
  };
  contactPerson?: {
    name?: string;
    email?: string;
    phone?: string;
  };
}

/**
 * Profile API.
 */
export const profileApi = {
  /**
   * Fetches user profile.
   */
  getProfile: async (): Promise<ProfileResponse> => {
    return api.get<ProfileResponse>(ENDPOINTS.PROFILE);
  },

  /**
   * Updates user profile.
   */
  updateProfile: async (data: ProfileUpdateData): Promise<{ success: boolean; message: string }> => {
    return api.put<{ success: boolean; message: string }>(ENDPOINTS.PROFILE, data)
      .catch((error) => ({
        success: false,
        message: error?.message || 'Failed to update profile',
      }));
  },

  /**
   * Changes user password.
   */
  changePassword: async (data: ChangePasswordFormData): Promise<{ success: boolean; message: string }> => {
    return api.post<{ success: boolean; message: string }>(ENDPOINTS.CHANGE_PASSWORD, data)
      .catch((error) => ({
        success: false,
        message: error?.message || 'Failed to change password',
      }));
  },

  /**
   * Downloads contract PDF.
   */
  downloadContractPDF: async (): Promise<Blob> => {
    const response = await apiClient.get('/profile/contract/download', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Gets the last payment transaction.
   */
  getLastTransaction: async (): Promise<TransactionResponse> => {
    return api.get<TransactionResponse>('/profile/transaction/last');
  },

  /**
   * Downloads invoice PDF.
   */
  downloadInvoicePDF: async (transactionId?: number): Promise<Blob> => {
    const url = transactionId 
      ? `/profile/invoice/download?id=${transactionId}` 
      : '/profile/invoice/download';
    const response = await apiClient.get(url, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Gets list of sponsored/referred Daies.
   */
  getSponsoredDaies: async (): Promise<SponsoredDaiesResponse> => {
    return api.get<SponsoredDaiesResponse>('/profile/sponsored-daies');
  },

  /**
   * Gets all payment transactions.
   */
  getTransactions: async (): Promise<TransactionsResponse> => {
    return api.get<TransactionsResponse>('/profile/transactions');
  },

  /**
   * Uploads document (IC/SSM photo).
   */
  uploadDocument: async (data: FormData): Promise<{ success: boolean; message: string; url?: string }> => {
    return api.post<{ success: boolean; message: string; url?: string }>('/profile/document/upload', data)
      .catch((error) => ({
        success: false,
        message: error?.message || 'Failed to upload document',
      }));
  },
};

/**
 * Transaction item type.
 */
export interface TransactionItem {
  id: number;
  paymentToken?: string;
  paymentAmount: string;
  paymentStatus: string;
  paidAt: string | null;
  createdAt: string;
  invoiceNumber: string;
  invoiceDate: string;
  registrationType: string;
  description: string;
  totalAmount: number;
  registrationLink?: {
    id: number;
    linkName: string;
  } | null;
}

/**
 * Transactions response type (multiple transactions).
 */
export interface TransactionsResponse {
  success: boolean;
  data?: TransactionItem[];
  message?: string;
}

/**
 * Transaction response type.
 */
export interface TransactionResponse {
  success: boolean;
  data?: {
    id: number;
    paymentToken: string;
    paymentAmount: string;
    paymentStatus: string;
    paidAt: string | null;
    createdAt: string;
    invoiceNumber: string;
    invoiceDate: string;
    registrationType: string;
    totalAmount: number;
    registrationLink: {
      id: number;
      linkName: string;
    } | null;
  };
  message?: string;
}

/**
 * Onboarding API.
 */
export const onboardingApi = {
  /**
   * Fetches onboarding status.
   */
  getStatus: async (): Promise<{ 
    completed: boolean; 
    currentStep: number;
    data?: {
      profile?: any;
      contract?: {
        daieName: string;
        systemId: string;
        startDate: string;
        contractType: string;
        content?: string; // Contract content from active template
      };
    };
  }> => {
    return api.get(ENDPOINTS.ONBOARDING);
  },

  /**
   * Saves onboarding progress.
   */
  saveProgress: async (data: object): Promise<void> => {
    return api.patch<void>(ENDPOINTS.ONBOARDING, data);
  },

  /**
   * Completes onboarding.
   */
  complete: async (data: object): Promise<void> => {
    return api.post<void>(`${ENDPOINTS.ONBOARDING}/complete`, data);
  },

  /**
   * Downloads contract PDF.
   */
  downloadContractPDF: async (): Promise<Blob> => {
    const response = await apiClient.get('/profile/contract/download', {
      responseType: 'blob',
    });
    return response.data;
  },
};

/**
 * Public Registration API.
 */
export const publicRegistrationApi = {
  /**
   * Validates a registration link token.
   */
  validateToken: async (token: string): Promise<{ 
    valid: boolean; 
    link?: {
      id: number;
      linkName: string;
      type: string;
      category: string;
      event_id?: number;
      discount_amount: number;
      discount_type?: string;
      discount?: {
        type: 'percentage' | 'amount';
        value: number;
        label: string;
      };
      expiry_date?: string;
      termsConditionsContent?: string;
      contractContent?: string;
      manager?: {
        id: number;
        name: string;
        region?: string;
        position?: string;
      };
    };
    message?: string;
  }> => {
    return api.get<{ 
      valid: boolean; 
      link?: {
        id: number;
        linkName: string;
        type: string;
        category: string;
        event_id?: number;
        discount_amount: number;
        discount_type?: string;
        discount?: {
          type: 'percentage' | 'amount';
          value: number;
          label: string;
        };
        expiry_date?: string;
        termsConditionsContent?: string;
        contractContent?: string;
        manager?: {
          id: number;
          name: string;
          region?: string;
          position?: string;
        };
      };
      message?: string;
    }>(
      `${ENDPOINTS.PUBLIC_REGISTER}/validate`,
      { params: { token } }
    );
  },

  /**
   * Saves registration details (user and daie profile) without payment.
   * Uses extended timeout (120s) because backend sends emails synchronously.
   */
  saveRegistrationDetails: async (data: FormData): Promise<{ success: boolean; daieId?: string; userId?: number }> => {
    const response = await apiClient.post<{ success: boolean; daieId?: string; userId?: number }>(
      `${ENDPOINTS.PUBLIC_REGISTER}/details`,
      data,
      { timeout: 120000 } // 120 seconds - extended timeout for file upload + email sending
    );
    return response.data;
  },

  /**
   * Submits registration form.
   */
  submitRegistration: async (data: FormData | object): Promise<{ success: boolean; daieId?: string; userId?: number }> => {
    // apiClient interceptor handles FormData Content-Type automatically
    return api.post<{ success: boolean; daieId?: string; userId?: number }>(
      ENDPOINTS.PUBLIC_REGISTER,
      data
    );
  },

  /**
   * Processes payment for registration.
   */
  processPayment: async (
    paymentData: object
  ): Promise<{ success: boolean; transactionId?: string }> => {
    return api.post<{ success: boolean; transactionId?: string }>(
      `${ENDPOINTS.PUBLIC_REGISTER}/payment`,
      paymentData
    );
  },

  /**
   * Validates a payment token.
   */
  validatePaymentToken: async (token: string): Promise<{ 
    valid: boolean; 
    payment?: {
      id: number;
      user_id: number;
      payment_amount: number;
      payment_status: string;
    };
    link?: {
      id: number;
      linkName: string;
      discount?: {
        type: 'percentage' | 'amount';
        value: number;
        label: string;
      };
      manager?: {
        id: number;
        name: string;
        region?: string;
        position?: string;
      };
    };
  }> => {
    return api.get<{ 
      valid: boolean; 
      payment?: {
        id: number;
        user_id: number;
        payment_amount: number;
        payment_status: string;
      };
      link?: {
        id: number;
        linkName: string;
        discount?: {
          type: 'percentage' | 'amount';
          value: number;
          label: string;
        };
        manager?: {
          id: number;
          name: string;
          region?: string;
          position?: string;
        };
      };
    }>(
      `${ENDPOINTS.PUBLIC_REGISTER}/payment/validate`,
      { params: { token } }
    );
  },

  /**
   * Sends login credentials email to user.
   */
  sendLoginCredentials: async (userId: number): Promise<{ success: boolean; message?: string }> => {
    return api.post<{ success: boolean; message?: string }>(
      `${ENDPOINTS.PUBLIC_REGISTER}/send-login-credentials`,
      { userId }
    );
  },
};

/**
 * Auth API.
 */
export const authApi = {
  /**
   * Login user.
   */
  login: async (credentials: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }): Promise<{ user: object; token: string; isNewUser?: boolean }> => {
    return api.post<{ user: object; token: string; isNewUser?: boolean }>(
      '/auth/login',
      credentials
    );
  },

  /**
   * Logout user.
   */
  logout: async (): Promise<void> => {
    return api.post<void>('/auth/logout');
  },

  /**
   * Request password reset.
   */
  forgotPassword: async (email: string): Promise<{ success: boolean; message: string }> => {
    return api.post<{ success: boolean; message: string }>('/auth/forgot-password', { email })
      .catch((error) => ({
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to send password reset link',
      }));
  },

  /**
   * Reset password with token.
   */
  resetPassword: async (token: string, password: string, email: string): Promise<{ success: boolean; message: string }> => {
    return api.post<{ success: boolean; message: string }>('/auth/reset-password', { token, password, email })
      .catch((error) => ({
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to reset password',
      }));
  },

  /**
   * Refresh access token.
   */
  refreshToken: async (): Promise<{ token: string }> => {
    return api.post<{ token: string }>('/auth/refresh');
  },

  /**
   * Admin: impersonate a user in readonly mode.
   */
  impersonate: async (
    userId: string | number
  ): Promise<{ user: any; token: string; impersonatedBy: { id: number; name: string; email: string } }> => {
    return api.post<{ user: any; token: string; impersonatedBy: { id: number; name: string; email: string } }>(
      `/auth/impersonate/${userId}`
    );
  },
};

